import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Question, SelectLang, } from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

// 导入封装的请求
import service from '@/services/sharepayx'

// 🌟 关键：定义 loginPath 变量（和你的登录页路由保持一致）
const loginPath = '/user/login'; // 你的登录页路由地址，根据实际情况调整
const isDev = process.env.NODE_ENV === 'development';
const defaultAvatar = "https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png";


// 封装：判断 10位秒级时间戳 是否过期（推荐抽离到 src/utils/auth.ts）
const isTokenExpired = () => {
    const expTimeStr = localStorage.getItem('spx_token_exp_time');
    if (!expTimeStr) return true; // 无过期时间视为过期

    // 🌟 核心：10位秒级时间戳 → 转换为毫秒级
    const expTime = Number(expTimeStr) * 1000;
    // 提前30秒判断过期（可选，提升体验）
    const now = Date.now();
    return now > expTime - 30 * 1000;
};

// 暂时关闭实时登录，返回虚拟假数据
export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
    loading?: boolean;
    fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {

    // 封装「校验登录状态」的逻辑
    const fetchUserInfo = async () => {
        try {
            const token = localStorage.getItem('spx_token');
            const expTimeStr = localStorage.getItem('spx_token_exp_time'); // 10位秒级时间戳
            if (!token) return undefined;

            // 🌟 修正：10位秒级 → 毫秒级
            if (expTimeStr) {
                const expTime = Number(expTimeStr) * 1000;
                if (Date.now() > expTime) {
                    localStorage.removeItem('spx_token');
                    localStorage.removeItem('spx_token_exp_time');
                    return undefined;
                }
            }

            // 调用获取用户信息接口（返回 BaseResponse 格式）
            const res = await service.users.fetchUserInfo();
            // 校验响应格式 + 状态码
            if (res && res.code === 200 && res.data) {
                console.log("appt ", res.data)
                return res.data; // 只返回 data 中的用户信息
            }

            // 响应异常：清除 Token 并返回 null
            localStorage.removeItem('spx_token');
            localStorage.removeItem('spx_token_exp_time');
            return undefined;
        } catch (error) {
            localStorage.removeItem('spx_token');
            localStorage.removeItem('spx_token_exp_time');
            return undefined;
        }
    };

    // 非登录页面：校验登录状态
    const { location } = history;
    const isLoginPage = [loginPath, '/user/register'].includes(location.pathname);
    if (!isLoginPage) {
        const currentUser = await fetchUserInfo();
        // 无用户信息（未登录/Token 无效）：跳登录页
        if (!currentUser) {
            history.push(loginPath);
            return {
                fetchUserInfo,
                settings: defaultSettings as Partial<LayoutSettings>,
                currentUser: undefined,
            };
        }
        // 有用户信息：初始化全局状态
        return {
            fetchUserInfo,
            currentUser,
            settings: defaultSettings as Partial<LayoutSettings>,
        };
    }

    return {
        fetchUserInfo,
        currentUser: undefined,
        settings: defaultSettings as Partial<LayoutSettings>,
    };
}

/**
 * 应用运行时布局配置
 * @param initialState 全局初始状态（如用户信息、系统设置等）
 * @param setInitialState 更新全局初始状态的方法
 * @returns 布局配置对象
 * 支持的api https://procomponents.ant.design/components/layout
 */
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState, }) => {
    return {
        // 配置页面右上角操作区的渲染内容（如文档、语言切换等）
        actionsRender: () => [
            <Question key="doc" />, // 提问/文档按钮，key 保证 React 元素唯一性
            <SelectLang key="SelectLang" />, // 语言选择组件
        ],
        // 配置头部头像的属性
        avatarProps: {
            src: initialState?.currentUser?.avatar || defaultAvatar, // 头像图片地址，从全局初始状态的当前用户信息中获取
            title: <AvatarName />, // 头像悬浮提示文字，使用自定义的 AvatarName 组件
            // 自定义头像的渲染逻辑，包裹头像内容到 AvatarDropdown 下拉组件中
            render: (_, avatarChildren) => {
                return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
            },
        },
        // 配置页面水印的属性
        waterMarkProps: {
            content: initialState?.currentUser?.username || "未登录用户",
        },
        // footerRender: () => <Footer />, // 注释表示不渲染页面底部 Footer 组件
        // 页面路由切换时触发的回调函数（强化 token 校验）
        onPageChange: () => {
            const { location } = history; // 获取当前路由位置信息

            const expTimeStr = localStorage.getItem('spx_token_exp_time');
            // 🌟 修正：10位秒级 → 毫秒级
            const isTokenExpired = expTimeStr ? Date.now() > Number(expTimeStr) * 1000 : true;
            // 核心逻辑：如果用户未登录，且当前路径不是登录页，则重定向到登录页
            // if (!initialState?.currentUser && location.pathname !== loginPath) {
            //     history.push(loginPath);
            // }
            // 核心逻辑：未登录 / token 过期，且不在登录页 → 重定向到登录页
            if ((!initialState?.currentUser || isTokenExpired) && !['/user/login', '/user/register'].includes(location.pathname)) {
                // 清除过期 token
                localStorage.removeItem('spx_token');
                localStorage.removeItem('spx_token_exp_time');
                // 更新全局状态
                setInitialState((prev) => ({ ...prev, currentUser: undefined }));
                // 跳转登录页并记录来源
                history.push({
                    pathname: loginPath,
                });
            }
        },
        // 配置布局背景图片列表（支持多张图片叠加）
        bgLayoutImgList: [
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
                left: 85,
                bottom: 100,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
                bottom: -68,
                right: -45,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
                bottom: 0,
                left: 0,
                width: '331px',
            },
        ],
        // 配置头部导航栏的链接列表（如 OpenAPI 文档入口）
        links: [
            <a href="https://docs.sharepayx.com" target="_blank" rel="noreferrer">
                {/* <BookOutlined /> */}
                <span>接口文档</span>
            </a>,
        ],        // 禁用菜单头部的默认渲染（menuHeaderRender 为 undefined 表示不渲染）
        menuHeaderRender: undefined,
        // 自定义 403 页面
        // unAccessible: <div>unAccessible</div>,
        // 增加一个 loading 的状态
        // 自定义子内容渲染逻辑（包裹页面主体内容）
        childrenRender: (children) => {
            // if (initialState?.loading) return <PageLoading />;
            return (
                <>
                    {children}
                    {isDev && (
                        <SettingDrawer
                            disableUrlParams // 禁用 URL 参数同步设置
                            enableDarkTheme // 启用暗黑主题切换
                            settings={initialState?.settings} // 传入当前系统设置
                            // 设置变更时更新全局初始状态
                            onSettingChange={(settings) => {
                                setInitialState((preInitialState) => ({
                                    ...preInitialState, // 保留原有状态
                                    settings, // 更新设置
                                }));
                            }}
                        />
                    )}
                </>
            );
        },
        // 合并初始状态中的自定义设置（覆盖默认布局配置）
        ...initialState?.settings,
    };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
    // 🌟 核心修改：根据环境动态设置 baseURL
    baseURL: "", // http://127.0.0.1:8081
    // 保留原有错误配置
    ...errorConfig,
    // 🌟 可选：添加请求拦截器（比如统一携带 Token，兼容你之前的需求）
    requestInterceptors: [
        (url, options) => {
            // 按需携带 Token（可选，根据你的业务补充）
            const token = localStorage.getItem('spx_token');
            const expTimeStr = localStorage.getItem('spx_token_exp_time');

            // 1. 提前校验 token 过期（提前 30 秒）
            if (expTimeStr) {
                const expTime = Number(expTimeStr) * 1000;
                if (Date.now() > expTime - 30 * 1000) {
                    localStorage.removeItem('spx_token');
                    localStorage.removeItem('spx_token_exp_time');
                    history.push({ pathname: loginPath, });
                }
            }

            if (token) {
                options.headers = {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                };
            }
            return { url, options };
        },
    ],
};